import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { sendPasswordReset } from "../../services/auth";

const LoginModal = ({ show, onClose }) => {
	const [isRegister, setIsRegister] = useState(false);
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [name, setName] = useState("");
	const [accessCode, setAccessCode] = useState("");
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");
	const [isForgotPassword, setIsForgotPassword] = useState(false);
	const [resetLoading, setResetLoading] = useState(false);
	const { login, signup, loading } = useAuth();

	const resetForm = () => {
		setEmail("");
		setPassword("");
		setName("");
		setAccessCode("");
		setError("");
		setSuccess("");
	};

	const toggleMode = () => {
		resetForm();
		setIsForgotPassword(false);
		setIsRegister((prev) => !prev);
	};

	const handleForgotPassword = async (e) => {
		if (e) e.preventDefault();
		setError("");
		setSuccess("");
		if (!email.trim()) {
			setError("Please enter your email address.");
			return;
		}
		setResetLoading(true);
		try {
			await sendPasswordReset(email.trim());
			setSuccess("Password reset email sent. Check your inbox.");
		} catch (error) {
			setError(
				error.message || "Failed to send reset email. Please try again.",
			);
		} finally {
			setResetLoading(false);
		}
	};

	const handleLogin = async (e) => {
		if (e) e.preventDefault();
		setError("");
		try {
			await login(email, password);
			onClose();
			resetForm();
		} catch (error) {
			setError(error.message || "Authentication failed. Please try again.");
		}
	};

	const handleRegister = async (e) => {
		if (e) e.preventDefault();
		setError("");
		setSuccess("");
		if (!name.trim() || !email.trim() || !password || !accessCode.trim()) {
			setError("Please fill in all fields.");
			return;
		}
		if (password.length < 6) {
			setError("Password must be at least 6 characters.");
			return;
		}
		try {
			await signup(email, password, name.trim(), accessCode.trim());
			onClose();
			resetForm();
		} catch (error) {
			setError(error.message || "Registration failed. Please try again.");
		}
	};

	const handleKeyPress = (e) => {
		if (e.key === "Enter") {
			isRegister ? handleRegister(e) : handleLogin(e);
		}
	};

	if (!show) return null;

	return (
		<div
			className="modal show d-block"
			style={{
				backgroundColor: "rgba(0,0,0,0.6)",
				backdropFilter: "blur(8px)",
			}}
		>
			<div
				className="modal-dialog modal-dialog-centered"
				style={{ maxWidth: "440px" }}
			>
				<div
					className="modal-content"
					style={{
						boxShadow: "0 12px 40px rgba(0,0,0,0.35)",
						background: "#f5e6d3",
						color: "#2d1b12",
						borderRadius: "16px",
						border: "none",
						overflow: "hidden",
					}}
				>
					{/* Header */}
					<div style={{ padding: "2rem 2rem 1rem", textAlign: "center" }}>
						<img
							src={process.env.PUBLIC_URL + "/EU59_LogoAsset 3@4x.png"}
							alt="Urban 59 Logo"
							style={{
								maxWidth: "140px",
								height: "auto",
								marginBottom: "0.75rem",
							}}
						/>
						<h5
							style={{
								color: "#2d1b12",
								fontWeight: "700",
								fontSize: "1.3rem",
								margin: 0,
							}}
						>
							{isForgotPassword
								? "Reset Password"
								: isRegister
									? "Create Account"
									: "Welcome Back"}
						</h5>
						{isForgotPassword && (
							<p
								style={{
									color: "#6b4c3b",
									fontSize: "0.85rem",
									marginTop: "0.5rem",
									marginBottom: 0,
								}}
							>
								Enter your email and we'll send you a link to reset your
								password.
							</p>
						)}
						{isRegister && (
							<p
								style={{
									color: "#6b4c3b",
									fontSize: "0.85rem",
									marginTop: "0.5rem",
									marginBottom: 0,
								}}
							>
								Enter your details and gym access code to get started.
							</p>
						)}
					</div>

					{/* Body */}
					<div style={{ padding: "1.25rem 2rem 2rem" }}>
						{error && (
							<div
								style={{
									background: "rgba(220, 53, 69, 0.1)",
									color: "#c0392b",
									border: "1px solid rgba(220, 53, 69, 0.25)",
									borderRadius: "8px",
									padding: "0.75rem 1rem",
									marginBottom: "1.25rem",
									fontSize: "0.875rem",
								}}
								role="alert"
							>
								{error}
							</div>
						)}
						{success && (
							<div
								style={{
									background: "rgba(25, 135, 84, 0.1)",
									color: "#1a7f4b",
									border: "1px solid rgba(25, 135, 84, 0.25)",
									borderRadius: "8px",
									padding: "0.75rem 1rem",
									marginBottom: "1.25rem",
									fontSize: "0.875rem",
								}}
								role="alert"
							>
								{success}
							</div>
						)}

						{isRegister && !isForgotPassword && (
							<div style={{ marginBottom: "1.25rem" }}>
								<label
									style={{
										display: "block",
										color: "#4a3228",
										fontWeight: "600",
										fontSize: "0.85rem",
										marginBottom: "0.4rem",
									}}
								>
									Full Name
								</label>
								<input
									type="text"
									value={name}
									onChange={(e) => setName(e.target.value)}
									onKeyPress={handleKeyPress}
									disabled={loading}
									placeholder="Enter your full name"
									style={{
										width: "100%",
										padding: "0.75rem 1rem",
										background: "#ffffff",
										border: "1.5px solid #d4b89c",
										borderRadius: "8px",
										color: "#2d1b12",
										fontSize: "0.95rem",
										outline: "none",
										transition: "border-color 0.2s",
										boxSizing: "border-box",
									}}
								/>
							</div>
						)}

						<div style={{ marginBottom: "1.25rem" }}>
							<label
								style={{
									display: "block",
									color: "#4a3228",
									fontWeight: "600",
									fontSize: "0.85rem",
									marginBottom: "0.4rem",
								}}
							>
								Email
							</label>
							<input
								type="email"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								onKeyPress={handleKeyPress}
								disabled={loading}
								placeholder="you@example.com"
								style={{
									width: "100%",
									padding: "0.75rem 1rem",
									background: "#ffffff",
									border: "1.5px solid #d4b89c",
									borderRadius: "8px",
									color: "#2d1b12",
									fontSize: "0.95rem",
									outline: "none",
									transition: "border-color 0.2s",
									boxSizing: "border-box",
								}}
							/>
						</div>

						{!isForgotPassword && (
							<div style={{ marginBottom: "1.25rem" }}>
								<label
									style={{
										display: "block",
										color: "#4a3228",
										fontWeight: "600",
										fontSize: "0.85rem",
										marginBottom: "0.4rem",
									}}
								>
									Password
								</label>
								<input
									type="password"
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									onKeyPress={handleKeyPress}
									disabled={loading}
									placeholder={
										isRegister ? "Min 6 characters" : "Enter your password"
									}
									style={{
										width: "100%",
										padding: "0.75rem 1rem",
										background: "#ffffff",
										border: "1.5px solid #d4b89c",
										borderRadius: "8px",
										color: "#2d1b12",
										fontSize: "0.95rem",
										outline: "none",
										transition: "border-color 0.2s",
										boxSizing: "border-box",
									}}
								/>
							</div>
						)}

						{isRegister && (
							<div style={{ marginBottom: "1.5rem" }}>
								<label
									style={{
										display: "block",
										color: "#4a3228",
										fontWeight: "600",
										fontSize: "0.85rem",
										marginBottom: "0.4rem",
									}}
								>
									Access Code
								</label>
								<input
									type="text"
									value={accessCode}
									onChange={(e) => setAccessCode(e.target.value)}
									onKeyPress={handleKeyPress}
									disabled={loading}
									placeholder="Enter gym access code"
									style={{
										width: "100%",
										padding: "0.75rem 1rem",
										background: "#ffffff",
										border: "1.5px solid #d4b89c",
										borderRadius: "8px",
										color: "#2d1b12",
										fontSize: "0.95rem",
										outline: "none",
										transition: "border-color 0.2s",
										boxSizing: "border-box",
									}}
								/>
							</div>
						)}

						{/* Primary button */}
						<button
							type="button"
							disabled={isForgotPassword ? resetLoading : loading}
							onClick={
								isForgotPassword
									? handleForgotPassword
									: isRegister
										? handleRegister
										: handleLogin
							}
							style={{
								width: "100%",
								padding: "0.85rem",
								background: "#e85d3f",
								color: "#ffffff",
								border: "none",
								borderRadius: "8px",
								fontWeight: "700",
								fontSize: "1rem",
								cursor: (isForgotPassword ? resetLoading : loading)
									? "not-allowed"
									: "pointer",
								opacity: (isForgotPassword ? resetLoading : loading) ? 0.7 : 1,
								marginBottom: "0.75rem",
								transition: "opacity 0.2s",
							}}
						>
							{isForgotPassword
								? resetLoading
									? "Sending..."
									: "Send Reset Link"
								: loading
									? "Please wait..."
									: isRegister
										? "Register"
										: "Login"}
						</button>

						{/* Close button */}
						<button
							type="button"
							onClick={() => {
								onClose();
								resetForm();
								setIsRegister(false);
								setIsForgotPassword(false);
							}}
							style={{
								width: "100%",
								padding: "0.75rem",
								background: "transparent",
								color: "#6b4c3b",
								border: "1.5px solid #c9a882",
								borderRadius: "8px",
								fontWeight: "600",
								fontSize: "0.95rem",
								cursor: "pointer",
								marginBottom: "1.5rem",
								transition: "background 0.2s",
							}}
						>
							Close
						</button>

						{/* Toggle link */}
						<div style={{ textAlign: "center" }}>
							{isForgotPassword ? (
								<span style={{ color: "#6b4c3b", fontSize: "0.9rem" }}>
									Remember your password?{" "}
									<button
										type="button"
										onClick={() => {
											setIsForgotPassword(false);
											setError("");
											setSuccess("");
										}}
										style={{
											background: "none",
											border: "none",
											color: "#e85d3f",
											fontWeight: "700",
											cursor: "pointer",
											textDecoration: "underline",
											padding: 0,
											fontSize: "0.9rem",
										}}
									>
										Back to Login
									</button>
								</span>
							) : isRegister ? (
								<span style={{ color: "#6b4c3b", fontSize: "0.9rem" }}>
									Already have an account?{" "}
									<button
										type="button"
										onClick={toggleMode}
										style={{
											background: "none",
											border: "none",
											color: "#e85d3f",
											fontWeight: "700",
											cursor: "pointer",
											textDecoration: "underline",
											padding: 0,
											fontSize: "0.9rem",
										}}
									>
										Login
									</button>
								</span>
							) : (
								<>
									<span style={{ color: "#6b4c3b", fontSize: "0.9rem" }}>
										<button
											type="button"
											onClick={() => {
												setIsForgotPassword(true);
												setError("");
												setSuccess("");
												setPassword("");
											}}
											style={{
												background: "none",
												border: "none",
												color: "#e85d3f",
												fontWeight: "700",
												cursor: "pointer",
												textDecoration: "underline",
												padding: 0,
												fontSize: "0.9rem",
											}}
										>
											Forgot Password?
										</button>
									</span>
									<br />
									<span
										style={{
											color: "#6b4c3b",
											fontSize: "0.9rem",
											marginTop: "0.5rem",
											display: "inline-block",
										}}
									>
										Need an account?{" "}
										<button
											type="button"
											onClick={toggleMode}
											style={{
												background: "none",
												border: "none",
												color: "#e85d3f",
												fontWeight: "700",
												cursor: "pointer",
												textDecoration: "underline",
												padding: 0,
												fontSize: "0.9rem",
											}}
										>
											Register
										</button>
									</span>
								</>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default LoginModal;
